import { useCallback } from 'react'

import {
  Stack,
  ListItem,
  ListItemProps,
  ListItemButton,
  ListItemAvatar,
  Typography
} from '@mui/material'

import { SearchDetailResult } from 'interface/search'
import { KojinResult } from 'interface/kojin'

import { RankChips } from './components/rankChips'
import { SenmoniChips } from './components/senmoniChips'
import { Seibetu } from './components/seibetu'
import { MrpStats } from './components/mrpStats'
import { ConventionChips } from './components/conventionChips'
import { GuideLineChips } from './components/guideLineChips'
import { TargetTypeChips } from './components/targetTypeChips'

import { KojinAvatar } from 'features/kojin/components/avatar'
import { KojinViewMode } from 'features/kojin/analytics/types'

type KojinRecordProps = {
  search: SearchDetailResult
  kojin: KojinResult
  rank?: number
  selected?: boolean
  onSelectedKojin?: (kojinCode: string) => void
  onSelectedAuthor?: (authorId: number) => void
  viewMode: KojinViewMode
}
const DcfKojinRecord = ({
  search,
  kojin,
  rank,
  selected,
  onSelectedKojin
}: KojinRecordProps) => {
  const onClickRecord = useCallback(
    () => {
      if (onSelectedKojin && kojin.kojinCode)  {
        onSelectedKojin(kojin.kojinCode)
      }
    },
    [onSelectedKojin, kojin.kojinCode]
  )
  return (
    <ListItemButton onClick={onClickRecord} selected={selected} alignItems="flex-start">
      <ListItemAvatar sx={{ minWidth: '42px', marginY: 0.5, textAlign: 'center' }}>
        <KojinAvatar kojin={kojin} rank={rank} globalRank={kojin.score?.journal?.rank || undefined}/>
      </ListItemAvatar>

      <Stack flexGrow={1} direction="column" spacing={0} component="div" marginLeft={1}>
        <Stack direction="row" spacing={0.5} sx={{ width: '100%' }} flexWrap="wrap" useFlexGap>
          <Stack flexGrow={1} direction="row" spacing={1} alignItems="baseline" borderBottom="1px solid #dcdcdc">
            <Typography variant="body2" color="text.primary" component="div">{kojin.isiName}</Typography>
            {kojin.nenrei !== null && kojin.nenrei !== undefined && (
              <Typography variant="caption" color="text.primary" component="div">{kojin.nenrei}歳</Typography>
            )}
            <Seibetu seibetuCode={kojin.seibetuCode}/>
            <MrpStats stats={kojin.mrpStats}/>
          </Stack>

          <Stack direction="row" spacing={1} padding={0} alignItems="center" justifyContent="flex-end">
            <ConventionChips variant='inherit' convention={kojin.convention}/>
            <GuideLineChips variant='inherit' guideLine={kojin.guideLine}/>
            <SenmoniChips search={search} senmoni={kojin.senmoni} size="small"/>
            <TargetTypeChips code={kojin.target?.targetType?.code} label={kojin.target?.targetType?.name}/>
            <RankChips variant="filled" color="journal" size="small" rank={kojin.score?.journal?.rank}/>
          </Stack>
        </Stack>

        {(kojin.organ && kojin.organ.length > 0) ? kojin.organ.map(
          (organ, i) => (
            <Stack key={`organ-${kojin.kojinCode}-${i}`} direction="row" spacing={1} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary" component="div">{organ.sisetuName}</Typography>
              {organ.bukaName && <Typography variant="caption" color="text.secondary" component="div">{organ.bukaName}</Typography>}
              {organ.yakusyokuName && <Typography variant="caption" color="text.secondary" component="div">{organ.yakusyokuName}</Typography>}
            </Stack>
          )
        ) : <></>}
      </Stack>
    </ListItemButton>
  )
}

const AuthorRecord = ({
  kojin,
  rank,
  selected,
  onSelectedAuthor
}: KojinRecordProps) => {
  const onClickRecord = useCallback(
    () => {
      if (onSelectedAuthor && kojin.authorId)  {
        onSelectedAuthor(kojin.authorId)
      }
    },
    [onSelectedAuthor, kojin.authorId]
  )
  return (
    <ListItemButton onClick={onClickRecord} selected={selected}>
      <Stack direction="row" width="100%" alignItems="center">
        <ListItemAvatar sx={{ minWidth: '42px', marginY: 0.5, textAlign: 'center' }}>
          <KojinAvatar kojin={kojin} rank={rank} globalRank={kojin.score?.journal?.rank || undefined}/>
        </ListItemAvatar>
        <Typography flexGrow={1} variant="body2" color="text.secondary" component="div" marginLeft={1} fontStyle="italic">{kojin.isiName}</Typography>
        <RankChips variant="filled" color="journal" size="small" rank={kojin.score?.journal?.rank}/>
      </Stack>
    </ListItemButton>
  )
}

export const KojinRecord = (props: KojinRecordProps & ListItemProps) => {
  const { kojin, ...others } = props
  return (
    <ListItem disablePadding {...others}>
      {kojin.kojinCode ? (
        <DcfKojinRecord {...props}/>
      ) : (
        <AuthorRecord {...props}/>
      )}
    </ListItem>
  )
}
